import React from 'react';
import loadable from '@loadable/component';
import { compose, setStatic } from 'recompose';
import { load as loadLocation } from '../stores/locations';
import { load as loadFormBootstrap } from '../stores/forms/seasonTickets';
import { loadListing } from '../stores/forms/checkout';
import { getConsents } from '../stores/consents';
import { load as loadAuth, logout } from '../stores/auth';
import { loadPartnerWhiteLabel } from '../stores/brand';
import { load as loadProfilePageData } from '../stores/forms/profile';
import { setDate } from '../stores/search';
import { getBooking } from '../stores/booking';

const Loading = () => {
  return <div className="p-loading" />;
};

export const Home = loadable(
  () => import(/* webpackChunkName: "page-home" */ './Home'),
  { fallback: <Loading /> }
);

export const Landing = compose(
  setStatic('fetchData', (store, route) =>
    store.dispatch(loadLocation(route.params.slug, route.params.country))
  )
)(
  loadable(() => import(/* webpackChunkName: "page-landing" */ './Landing'), {
    fallback: <Loading />
  })
);

export const LoggedInPage = loadable(
  () => import(/* webpackChunkName: "page-logged-in" */ './LoggedInPage'),
  {
    fallback: <Loading />
  }
);

export const SeasonTicket = compose(
  setStatic('fetchData', (store, match) =>
    store.dispatch(loadFormBootstrap(match.params.vendor))
  )
)(
  loadable(
    () => import(/* webpackChunkName: "page-season-ticket" */ './SeasonTicket'),
    {
      fallback: <Loading />
    }
  )
);

const setDatesPromise = (dispatch, queryParams) => {
  const { arriving, leaving, monthly_duration: monthlyDuration } = queryParams;

  const hasArriving = arriving !== undefined;
  const hasLeavingOrDuration =
    leaving !== undefined || monthlyDuration !== undefined;

  const shouldSetDates = hasArriving && hasLeavingOrDuration;

  return shouldSetDates
    ? dispatch(
        setDate([
          {
            arriving,
            leaving: leaving || null,
            monthlyDuration: monthlyDuration || null
          }
        ])
      )
    : Promise.resolve();
};

export const SearchResults = compose(
  setStatic('fetchData', (store, match, queryParams) => {
    return setDatesPromise(store.dispatch, queryParams);
  })
)(
  loadable(
    () =>
      import(/* webpackChunkName: "page-search-results" */ './SearchResults'),
    {
      fallback: <Loading />
    }
  )
);

export const PartnerSearchResults = compose(
  setStatic('fetchData', (store, match, queryParams) => {
    const { partnerName } = match.params;

    return Promise.all([
      store.dispatch(loadPartnerWhiteLabel(partnerName)),
      setDatesPromise(store.dispatch, queryParams)
    ]);
  })
)(
  loadable(
    () =>
      import(/* webpackChunkName: "page-search-results" */ './PartnerSearchResults'),
    {
      fallback: <Loading />
    }
  )
);

export const LoginWall = compose(
  setStatic('fetchData', (store, match, queryParams) =>
    store.dispatch(getConsents(queryParams, false, match))
  )
)(
  loadable(
    () => import(/* webpackChunkName: "page-login-wall" */ './LoginWall'),
    {
      fallback: <Loading />
    }
  )
);

export const Register = loadable(
  () => import(/* webpackChunkName: "page-register" */ './Register'),
  {
    fallback: <Loading />
  }
);

export const Checkout = compose(
  setStatic('fetchData', (store, match) => {
    const listingId = parseInt(match.params.listingId, 10);
    return store.dispatch(loadListing(listingId));
  })
)(
  loadable(() => import(/* webpackChunkName: "page-checkout" */ './Checkout'), {
    fallback: <Loading />
  })
);

export const PartnerCheckout = compose(
  setStatic('fetchData', (store, match) => {
    const listingId = parseInt(match.params.listingId, 10);
    const { partnerName } = match.params;

    const whiteLabel = store.dispatch(loadPartnerWhiteLabel(partnerName));
    const listing = store.dispatch(loadListing(listingId));

    return Promise.all([whiteLabel, listing]);
  })
)(
  loadable(
    () => import(/* webpackChunkName: "page-checkout" */ './PartnerCheckout'),
    {
      fallback: <Loading />
    }
  )
);

export const ConsentsWall = compose(
  setStatic('fetchData', (store, match, queryParams) =>
    store.dispatch(getConsents(queryParams, false, match))
  )
)(
  loadable(
    () => import(/* webpackChunkName: "page-consent" */ './ConsentWall'),
    {
      fallback: <Loading />
    }
  )
);

export const Revoke = compose(
  setStatic('fetchData', store =>
    store.dispatch(loadAuth()).then(() => store.dispatch(getConsents()))
  )
)(
  loadable(() => import(/* webpackChunkName: "page-revoke" */ './Revoke'), {
    fallback: <Loading />
  })
);

export const AddListing = loadable(
  () =>
    import(/* webpackChunkName: "page-add-listing" */ './dashboard/AddListing'),
  {
    fallback: <Loading />
  }
);

export const EditListing = loadable(
  () =>
    import(/* webpackChunkName: "page-edit-listing" */ './dashboard/EditListing'),
  {
    fallback: <Loading />
  }
);

export const UpdatePrompt = loadable(
  () =>
    import(/* webpackChunkName: "page-update-prompt" */ './dashboard/UpdatePrompt'),
  {
    fallback: <Loading />
  }
);

export const ParkingSpaces = loadable(
  () =>
    import(/* webpackChunkName: "page-parking-spaces" */ './dashboard/ParkingSpaces'),
  {
    fallback: <Loading />
  }
);

export const Availability = loadable(
  () =>
    import(/* webpackChunkName: "page-availability" */ './dashboard/Availability'),
  {
    fallback: <Loading />
  }
);

export const ReferFriend = loadable(
  () => import(/* webpackChunkName: "page-refer" */ './ReferFriend'),
  {
    fallback: <Loading />
  }
);

export const VehiclePage = loadable(
  () =>
    import(/* webpackChunkName: "page-vehicle" */ './dashboard/VehiclePage'),
  {
    fallback: <Loading />
  }
);

export const PaymentsPage = loadable(
  () =>
    import(/* webpackChunkName: "page-payments" */ './dashboard/PaymentsPage'),
  {
    fallback: <Loading />
  }
);

export const WithdrawalPage = loadable(
  () =>
    import(/* webpackChunkName: "page-withdrawal" */ './dashboard/WithdrawalPage'),
  {
    fallback: <Loading />
  }
);

export const BookingsMadePage = loadable(
  () =>
    import(/* webpackChunkName: "page-bookings-made" */ './dashboard/BookingsMadePage'),
  {
    fallback: <Loading />
  }
);

export const BookingsDetailsPage = compose(
  setStatic('fetchData', (store, match) => {
    const {
      params: { bookingId }
    } = match;

    return Promise.all([
      store.dispatch(loadAuth()),
      store.dispatch(getBooking(bookingId))
    ]);
  })
)(
  loadable(
    () =>
      import(/* webpackChunkName: "page-booking-details" */ './dashboard/BookingDetailsPage'),
    {
      fallback: <Loading />
    }
  )
);

export const ProfilePage = compose(
  setStatic('fetchData', store => {
    return store.dispatch(loadAuth()).then(() => {
      if (!store.getState().auth.user) {
        return null;
      }

      return Promise.all([
        store.dispatch(getConsents()),
        store.dispatch(loadProfilePageData())
      ]);
    });
  })
)(
  loadable(
    () =>
      import(/* webpackChunkName: "page-edit-profile" */ './dashboard/ProfilePage'),
    {
      fallback: <Loading />
    }
  )
);

export const Logout = compose(
  setStatic('fetchData', store => {
    return store.dispatch(logout());
  })
)(
  loadable(() => import(/* webpackChunkName: "page-logout" */ './Logout'), {
    fallback: <Loading />
  })
);

export const Agreements = loadable(
  () =>
    import(/* webpackChunkName: "page-agreements" */ './policies/AgreementsPage'),
  {
    fallback: <Loading />
  }
);

export const Cancellation = loadable(
  () =>
    import(/* webpackChunkName: "page-cancellation" */ './policies/CancellationPage'),
  {
    fallback: <Loading />
  }
);

export const Privacy = loadable(
  () => import(/* webpackChunkName: "page-privacy" */ './policies/PrivacyPage'),
  {
    fallback: <Loading />
  }
);

export const Terms = loadable(
  () => import(/* webpackChunkName: "page-terms" */ './policies/TermsPage'),
  {
    fallback: <Loading />
  }
);
